<template>
  <div class="detail-container" ref="contentContainer">
    <el-container class="product-detail-page" style="height: auto">
      <Header :scrollPosition="scrollPosition" />
      <el-main class="main-content">
        <!-- 商品图片区 -->
        <div class="banner-container" ref="carouselContainer">
          <el-carousel
            height="400px"
            :autoplay="true"
            :interval="5000"
            :loop="true"
            arrow="never"
            ref="carouselRef"
            indicator-position="none"
          >
            <el-carousel-item
              v-for="(item, index) in product.main_image"
              :key="index"
            >
              <div class="banner-link">
                <el-image
                  :src="item"
                  class="banner-img"
                  fit="cover"
                  :alt="item.title"
                  @load="handleImageLoad"
                >
                  <template #placeholder>
                    <div class="image-slot">加载中...</div>
                  </template>
                  <template #error>
                    <div class="image-slot image-error-slot">
                      <img
                        :src="require('@/assets/author.png')"
                        alt="图片加载失败"
                      />
                    </div>
                  </template>
                </el-image>
              </div>
            </el-carousel-item>
          </el-carousel>
        </div>

        <!-- 价格与销量区 -->
        <el-card class="price-card" body-class="price-body-container">
          <div class="price-container">
            <div class="price-box">
              <div>
                <span class="current-price">¥{{ product.price }}</span>
                <span
                  class="original-price"
                  v-if="product.cost_price !== '0.00'"
                  >¥{{ product.cost_price }}</span
                >
              </div>

              <div class="sales-info">
                <span>已售{{ formattedSalesCount }}</span>
              </div>
            </div>

            <h2 class="product-name">{{ product.name }}</h2>
          </div>
        </el-card>

        <!-- 商品描述区 -->
        <el-row class="detail-title">
          <el-col :span="24" class="detail-title-text">
            <p>——</p>
            <p>图文详情</p>
            <p>——</p>
          </el-col>
        </el-row>
        <!-- 商品详情区 -->
        <el-card class="detail-card" body-class="price-body-container">
          <el-row :gutter="10">
            <el-col :span="24">
              <div
                class="product-content-box markdown-body"
                v-if="product.content"
                v-html="product.content"
              ></div>
              <div class="content-loading" v-else>
                <p>详情内容加载中...</p>
              </div>
            </el-col>
          </el-row>
        </el-card>
        <ConfirmOrderPopup
          :isShow="showOrderPopup"
          :goods="product"
          :isLoading="orderPopupLoading"
          :discount="0"
          @close="handlePopupClose"
          @submit="handleOrderSubmit"
        />
      </el-main>

      <el-footer height="50px" class="footer-actions" v-if="btnShowStatus">
        <el-row class="footer-buttons">
          <el-col :span="24" class="buy-button-container">
            <el-button
              type="danger"
              class="buy-button"
              @click="handleBuyClick"
              round
            >
              立即购买
            </el-button>
          </el-col>
        </el-row>
      </el-footer>

      <div
        v-if="showSuccessImg"
        class="success-img-container"
        @click="backToHome"
      >
        <img src="@/assets/payover.jpg" alt="支付成功" class="success-img" />
      </div>
    </el-container>
  </div>
</template>

<script setup>
import { useFetch } from "@/composables/useFetch.js";
import { getShangPingList, pay } from "@/api/product.js";
import { useRouter, useRoute } from "vue-router";
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { ElMessage } from "element-plus";
import { setWxShare } from "@/api/wxShare";
import Header from "@/components/util/Header";
import ConfirmOrderPopup from "@/components/ConfirmOrderPopup";

const router = useRouter();
const route = useRoute();
const payType = ref("");
const shangpuNo = ref("");
const shangpingNo = ref("");
const product = ref({});
const showOrderPopup = ref(false);
const orderPopupLoading = ref(false);
const showSuccessImg = ref(false);
const btnShowStatus = ref(true);
const storage = window.localStorage;

// 轮播组件和容器的ref
const carouselRef = ref(null);
const carouselContainer = ref(null);
const datas = ref({
  bannerList: [], // 轮播图列表数据
});

const startX = ref(0);
const minSwipeDistance = ref(30);

const contentContainer = ref(null);
const scrollPosition = ref(0);

const handleScroll = () => {
  if (contentContainer.value) {
    scrollPosition.value = contentContainer.value.scrollTop || 0;
  }
};

const handlePopupClose = () => {
  showOrderPopup.value = false;
  orderPopupLoading.value = false;
};

const handleBuyClick = () => {
  const token = localStorage.getItem("usertoken");
  if (!token) {
    // 用户未授权，跳转到授权页面
    const redirectUrl = encodeURIComponent(route.fullPath);
    router.push({
      name: "Auth",
      query: { redirect: redirectUrl },
    });
    return;
  }

  // 已授权，则弹出购买弹窗
  showOrderPopup.value = true;
};

const backToHome = () => {
  showSuccessImg.value = false; // 隐藏图片
  router.push("/");
};

const productFetch = useFetch(async () => {
  const response = await getShangPingList(
    payType.value,
    shangpuNo.value,
    shangpingNo.value
  );

  if (response.status) {
    product.value = response.shangping;
  } else {
    btnShowStatus.value = false;
    ElMessage.error(response.msg);
  }
}, null);

const payFetch = useFetch(async (params) => {
  const response = await pay(params);
  if (response.status) {
    ElMessage.success(response.msg);
  } else {
    ElMessage.warning(response.msg);
  }
  return response;
}, null);

const getParams = () => {
  payType.value = route.query.type || "";
  shangpuNo.value = route.query.shangpu_no || "";
  shangpingNo.value = route.query.shangping_no || "";
};

const handleOrderSubmit = async (payInfo) => {
  orderPopupLoading.value = true;
  // 1. 基础校验
  if (!product.value) {
    ElMessage.warning("商品信息不存在，无法提交订单");
    return;
  }
  if (
    !payInfo.user_name ||
    !payInfo.user_phone ||
    !payInfo.city ||
    !payInfo.user_address
  ) {
    ElMessage.warning("请完善收货信息后再提交");
    return;
  }
  if (payInfo.count <= 0) {
    ElMessage.warning("购买数量必须大于0");
    return;
  }

  // 2. 组装请求参数
  const params = {
    pay_type: payType.value,
    shangpu_no: shangpuNo.value,
    shangping_no: shangpingNo.value,
    count: payInfo.count,
    total: payInfo.total,
    user_name: payInfo.user_name,
    user_address: payInfo.user_address,
    user_phone: payInfo.user_phone,
    city: payInfo.city,
    remark: payInfo.remark || "", // 备注可选
    usertoken: storage.usertoken,
  };

  try {
    // 3. 显示加载状态
    const loadingInstance = ElMessage({
      message: "正在创建订单...",
      type: "success",
      duration: 0, // 不自动关闭
      background: "rgba(0, 0, 0, 0.7)",
    });

    // 4. 调用下单接口
    const result = await payFetch.execute(params);
    console.log(result);

    // 5. 关闭加载状态
    loadingInstance.close();

    // 6. 校验接口返回
    if (!result.status) {
      ElMessage.error(result.msg || "下单失败，请重试");
      return;
    }

    // 7. 提取微信支付参数（接口返回的 data 字段）
    const {
      appId,
      timeStamp,
      nonceStr,
      package: prepayPackage,
      paySign,
      signType,
    } = result.data;

    console.log(prepayPackage);

    // 8. 校验支付参数完整性
    if (!appId || !timeStamp || !nonceStr || !prepayPackage || !paySign) {
      ElMessage.error("支付参数不完整，无法发起支付");
      console.error("缺少微信支付参数:", result.data);
      return;
    }

    // 9. 调起微信 JSAPI 支付（需确保页面在微信浏览器中）
    // eslint-disable-next-line
    if (typeof WeixinJSBridge === "undefined") {
      // 非微信浏览器环境提示
      ElMessage.error("请在微信浏览器中打开以完成支付");
      return;
    }

    // 10. 调用微信支付接口
    // eslint-disable-next-line
    WeixinJSBridge.invoke(
      "getBrandWCPayRequest",
      {
        appId, // 公众号ID
        timeStamp, // 时间戳（字符串类型）
        nonceStr, // 随机串
        package: prepayPackage, // 预支付交易会话标识（prepay_id=xxx）
        signType: signType || "MD5", // 签名方式
        paySign, // 支付签名
      },
      function (res) {
        // 11. 支付结果回调
        if (res.err_msg === "get_brand_wcpay_request:ok") {
          // 支付成功
          // ElMessage.success("支付成功！");
          showSuccessImg.value = true;
          // 跳转到订单详情页或首页（根据业务需求）
          // route.push({
          //   path: "/order/detail",
          //   query: { orderNo: result.data.orderNo },
          // });
        } else if (res.err_msg === "get_brand_wcpay_request:cancel") {
          // 支付取消
          ElMessage.info("您已取消支付");
        } else {
          // 支付失败
          ElMessage.error("支付失败，请重试");
          console.error("微信支付失败:", res.err_msg);
        }
      }
    );
  } catch (err) {
    // 12. 捕获接口调用异常
    ElMessage.close();
    ElMessage.error("创建订单失败，请稍后重试");
    console.error("支付接口调用失败:", err);
  } finally {
    handlePopupClose();
  }
};

onMounted(() => {
  setWxShare();
  initTouchEvent();

  if (contentContainer.value) {
    contentContainer.value.addEventListener("scroll", handleScroll);
  }

  getParams();
});

onUnmounted(() => {
  setWxShare();
  const container = carouselContainer.value;
  if (container) {
    container.removeEventListener("touchstart", handleTouchStart);
    container.removeEventListener("touchmove", handleTouchMove);
    container.removeEventListener("touchend", handleTouchEnd);
  }

  if (contentContainer.value) {
    contentContainer.value.removeEventListener("scroll", handleScroll);
  }
});

const handleImageLoad = () => {
  // 检查轮播组件是否存在
  if (!carouselRef.value) return;

  const carousel = carouselRef.value;
  const total = datas.value.bannerList.length;
  // 没有轮播数据则返回
  if (total === 0) return;

  let currentIndex = 0;
  // 获取当前激活的索引（Element Plus的ElCarousel中activeIndex是响应式属性）
  if (typeof carousel.activeIndex === "number") {
    // 处理循环轮播时的索引计算（避免负数或超出范围）
    currentIndex = Math.floor(carousel.activeIndex) % total;
    currentIndex = currentIndex < 0 ? total + currentIndex : currentIndex;
  }

  // 确保索引是有效的整数
  currentIndex = Number.parseInt(currentIndex, 10);
  if (currentIndex >= 0 && currentIndex < total) {
    // 设置激活的轮播项（Element Plus中用setActiveItem方法）
    carousel.setActiveItem(currentIndex);
  }

  // 如需设置高度自适应，可取消注释
  // if (carousel.$el) {
  //   carousel.$el.style.height = 'auto';
  // }
};

const initTouchEvent = () => {
  // 获取容器DOM元素
  const container = carouselContainer.value;
  if (container) {
    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("touchmove", handleTouchMove);
    container.addEventListener("touchend", handleTouchEnd);
  }
};

const handleTouchStart = (e) => {
  startX.value = e.changedTouches[0].pageX;
};

const handleTouchMove = () => {
  // 例如：限制只能水平滑动
  // if (Math.abs(垂直滑动距离) > 水平滑动距离) 可忽略
};

// 触摸结束事件
const handleTouchEnd = (e) => {
  const endX = e.changedTouches[0].pageX;
  const distance = endX - startX.value;

  // 触发轮播切换
  if (distance > minSwipeDistance.value) {
    carouselRef.value.prev(); // 上一张
  } else if (distance < -minSwipeDistance.value) {
    carouselRef.value.next(); // 下一张
  }

  // 重置起始位置
  startX.value = 0;
};

const formattedSalesCount = computed(() => {
  const count = product.value.sales_count || 0;

  if (count < 10000) {
    return `${count}`; // 小于1万：直接显示）
  }

  // 转换为“万”单位
  const inTenThousand = count / 10000;

  // 关键优化：判断是否为整数，整数则不显示小数
  if (Number.isInteger(inTenThousand)) {
    return `${inTenThousand}万`;
  } else {
    return `${inTenThousand.toFixed(1)}万`;
  }
});

watch(
  () => route.query,
  (newQuery) => {
    payType.value = newQuery.type || "";
    shangpuNo.value = newQuery.shangpu_no || "";
    shangpingNo.value = newQuery.shangping_no || "";

    if (shangpuNo.value && shangpingNo.value) {
      productFetch.execute();
    } else {
      productFetch.error = "缺少必要的参数";
    }
  },
  { immediate: true }
);
</script>

<style lang="less" scoped>
@import "~@/styles/mixin.less";
@primary-color: #ff4d4f;
@secondary-color: #1890ff;
@text-color: #999;
@text-color-secondary: #595959;
@text-color-placeholder: #8c8c8c;
@border-color: #e8e8e8;
@border-color-light: #f0f0f0;
@bg-color: #f5f5f5;
@bg-color-highlight: #f1f1f1;
@bg-color-selected: #e6f7ff;
@shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
@radius: 10px;

.page-loading-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.loading-text {
  margin-top: 16px;
  color: #666;
  font-size: 16px;
}

.content-loading {
  width: 100%;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;
  font-size: 14px;
}

.card-common() {
  margin-bottom: 16px;
  border-radius: @radius;
  box-shadow: @shadow;
  border: none;
  width: 96%;
  margin: 10px auto;
}

.flex-center() {
  display: flex;
  align-items: center;
}

.detail-container {
  position: relative;
  margin: auto;
  width: 100%;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.product-detail-page {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  padding: 0;

  .banner-container {
    width: 100%;
    height: 400px;
    overflow: hidden;
    position: relative;

    .el-carousel {
      width: 100%;
      height: 100%;
    }

    .el-carousel__item {
      width: 100%;
      height: 100%;
    }

    .banner-link {
      display: block;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }

    .banner-img {
      width: 100%;
      height: 400px;

      object-fit: cover;
    }

    .image-slot {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      color: #999;
      font-size: 14px;

      &.image-error-slot {
        img {
          width: 100%;
          height: 200px;
          object-fit: contain;
        }
      }
    }

    .el-carousel__indicators {
      bottom: 10px;

      .el-carousel__indicator {
        width: 8px;
        height: 8px;
        margin: 0 4px;
        background-color: rgba(255, 255, 255, 0.5);

        &.is-active {
          width: 24px;
          border-radius: 4px;
          background-color: #fff;
        }
      }
    }
  }

  .price-card,
  .detail-card {
    .card-common();
  }

  .detail-card {
    width: 100%;
    border-radius: 0;
    box-shadow: none;
  }

  .price-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 5px;
    .price-box {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }
    .current-price {
      color: @primary-color;
      font-size: 24px;
      margin-right: 10px;
    }

    .original-price {
      color: @text-color-placeholder;
      text-decoration: line-through;
      font-size: 14px;
    }

    .sales-info {
      color: @text-color-placeholder;
      font-size: 14px;
      .flex-center();
      gap: 8px;
    }
  }

  .product-name {
    text-align: left;
    font-size: 18px;
    line-height: 1.5;
  }

  .detail-title {
    text-align: center;
    font-size: 14px;
    width: 60%;
    margin: auto;
    color: @text-color;
    background-color: transparent;
    .detail-title-text {
      display: flex;
      gap: 5px;
      align-items: center;
      justify-content: center;
    }
  }
}

.footer-actions {
  background-color: #fff;
  border-top: 1px solid @border-color;
  padding: 0;
  position: sticky;
  bottom: 0;
  z-index: 10;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px));
  min-height: calc(50px + env(safe-area-inset-bottom));

  .footer-buttons {
    height: 100%;
    .buy-button-container {
      display: flex;
      align-items: center;
      justify-content: center;
      .buy-button {
        width: 90%;
        height: 80%;
        font-size: 16px;
      }
    }
  }
}

.success-img-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  cursor: pointer;
}

/* 支付成功图片样式 */
.success-img {
  width: 280px;
  height: auto;
  max-width: 80%;
  object-fit: contain;
  border-radius: 10px;
}

@media (max-width: 768px) {
  .current-price {
    font-size: 20px !important;
  }

  .product-name {
    font-size: 16px !important;
  }

  .detail-image {
    height: 140px !important;
  }
}
</style>
